// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: scripts and stylesheets an HTML page loads from a few CDNs (`<script
// src="https://cdn.tailwindcss.com">`, Leaflet or Chart.js from unpkg or jsDelivr), so pages people
// send each other look as they do in a browser. The viewer's policy, and Konvo's own that the frame
// inherits, block every web address, so Konvo's API fetches the files (only from the CDNs in
// HtmlPages.ts) and they go into the page as inline `<script>` and `<style>`. Pictures from those
// CDNs and from map tile servers come through the media proxy (PageWebFiles.ts); everything else
// stays blocked.

import {HTML_LIBRARY_HOSTS, type HtmlLibrary, htmlLibraryUrl} from '@konvo/schema/src/domains/konvo/HtmlPages';

/** Gives a CDN file as fetched (null: it can't be had). */
export type HtmlLibraryLoader = (url: string) => Promise<HtmlLibrary | null>;

/** Files one page may load from the CDNs. */
const LIBRARIES_MAX = 16;
const SCRIPT_TYPES = new Set(['', 'text/javascript', 'application/javascript', 'module']);
/** Attributes that belong to fetching the file, not to the script it holds. */
const FETCH_ATTRIBUTES = [
	'src',
	'integrity',
	'crossorigin',
	'async',
	'defer',
	'referrerpolicy',
	'fetchpriority',
	'nonce',
];
const CSS_URL = /url\(\s*(['"]?)([^'")]+?)\1\s*\)/gi;
const LEAFLET = /\/leaflet(@[^/]*)?\/dist\/leaflet(-src)?\.js$/;

const escapeHosts = HTML_LIBRARY_HOSTS.map((host) => host.replace(/\./g, '\\.')).join('|');
const NAMES_LIBRARY = new RegExp(`\\b(src|href)\\s*=\\s*["']?\\s*(https:)?//(${escapeHosts})(?=[/"'\\s>]|$)`, 'i');

/** Whether the page names a script or stylesheet on one of the CDNs (a quick look at the markup). */
export function pageUsesWebLibraries(html: string): boolean {
	return NAMES_LIBRARY.test(html);
}

/** Keeps a file's text from closing the element it goes into, or opening an HTML comment in it. */
const rawText = (text: string, element: 'script' | 'style') =>
	text
		.replace(new RegExp(`</(${element})`, 'gi'), '<\\/$1')
		.replace(/<!--/g, element === 'script' ? '<\\x21--' : '<\\!--');

/** A stylesheet's relative `url()`s, made absolute against where it came from (they're pictures and fonts). */
function absoluteCssUrls(css: string, base: string): string {
	return css.replace(CSS_URL, (whole, quote: string, ref: string) => {
		const value = ref.trim();
		if (/^(data:|#|[a-z][a-z0-9+.-]*:)/i.test(value)) return whole;
		try {
			return `url(${quote}${new URL(value, base).href}${quote})`;
		} catch {
			return whole;
		}
	});
}

/** What a script element runs, when it's one the page loads from a CDN: its address; else null. */
function scriptLibrary(element: HTMLScriptElement): string | null {
	const type = (element.getAttribute('type') ?? '').trim().toLowerCase();
	if (!SCRIPT_TYPES.has(type)) return null;
	return htmlLibraryUrl(element.getAttribute('src') ?? '');
}

function stylesheetLibrary(element: HTMLLinkElement): string | null {
	const rel = (element.getAttribute('rel') ?? '').toLowerCase().split(/\s+/);
	if (!rel.includes('stylesheet') || rel.includes('alternate')) return null;
	return htmlLibraryUrl(element.getAttribute('href') ?? '');
}

/**
 * The page with the scripts and stylesheets it loads from the CDNs in it. A script keeps its place
 * (a deferred one moves to the end of the body, where it runs at the same point) and its `onload`
 * runs right after it; a file that can't be had stays as it was (blocked, as before).
 */
export async function prepareWebLibraries(html: string, load: HtmlLibraryLoader): Promise<string> {
	const doc = new DOMParser().parseFromString(html, 'text/html');
	const scripts = [...doc.querySelectorAll('script[src]')].flatMap((element) => {
		const url = scriptLibrary(element as HTMLScriptElement);
		return url ? [{element: element as HTMLScriptElement, url}] : [];
	});
	const sheets = [...doc.querySelectorAll('link[href]')].flatMap((element) => {
		const url = stylesheetLibrary(element as HTMLLinkElement);
		return url ? [{element, url}] : [];
	});
	const urls = [...new Set([...scripts, ...sheets].map(({url}) => url))].slice(0, LIBRARIES_MAX);
	if (!urls.length) return html;
	const files = new Map(await Promise.all(urls.map(async (url) => [url, await load(url).catch(() => null)] as const)));

	for (const {element, url} of sheets) {
		const file = files.get(url);
		if (file?.type !== 'style') continue;
		const style = doc.createElement('style');
		for (const name of ['media', 'title', 'id']) {
			const value = element.getAttribute(name);
			if (value !== null) style.setAttribute(name, value);
		}
		style.textContent = rawText(absoluteCssUrls(file.text, file.url), 'style');
		element.replaceWith(style);
	}

	const deferred: Array<HTMLScriptElement> = [];
	for (const {element, url} of scripts) {
		const file = files.get(url);
		if (file?.type !== 'script') continue;
		const script = doc.createElement('script');
		for (const {name, value} of [...element.attributes]) {
			if (!FETCH_ATTRIBUTES.includes(name.toLowerCase()) && name.toLowerCase() !== 'onload')
				script.setAttribute(name, value);
		}
		let text = file.text;
		// Leaflet finds its marker pictures from its stylesheet's address, which is a proxy address
		// here; point it at the CDN, whose pictures come through the proxy as the page sets them.
		if (LEAFLET.test(new URL(file.url).pathname))
			text += `\n;if(window.L&&L.Icon&&L.Icon.Default&&!L.Icon.Default.imagePath)L.Icon.Default.imagePath=${JSON.stringify(new URL('images/', file.url).href)};`;
		const onload = element.getAttribute('onload');
		if (onload) text += `\n;(function(event){\n${onload}\n}).call(document.currentScript,new Event('load'));`;
		script.textContent = rawText(text, 'script');
		const isDeferred = element.hasAttribute('defer') && !element.hasAttribute('async') && script.type !== 'module';
		if (isDeferred) {
			element.remove();
			deferred.push(script);
		} else element.replaceWith(script);
	}
	for (const script of deferred) doc.body.append(script);
	return `<!doctype html>\n${doc.documentElement.outerHTML}`;
}
